import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, object = signed in
  const [error, setError] = useState("");

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data || false);
      if (data && data.language) {
        try {
          // Only seed localStorage from the server when the user hasn't already
          // made a local choice — otherwise we'd silently overwrite the value
          // the LanguageToggle just wrote and revert on next full load.
          if (!localStorage.getItem("lang")) {
            localStorage.setItem("lang", data.language);
          }
        } catch (storageErr) {
          console.debug("localStorage lang persist failed", storageErr);
        }
      }
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const register = useCallback(async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Non-fatal: proceed to clear local session even if server call fails.
      console.warn("logout call failed:", e?.message);
    }
    setUser(false);
  }, []);

  // Actions never change identity — bundle them once so consumers subscribed only
  // to actions don't re-render on user changes. State (user/error) is memoized
  // separately with its own tight dep list.
  const actions = useMemo(
    () => ({ setUser, login, register, logout, refresh: fetchMe }),
    [login, register, logout, fetchMe]
  );
  const value = useMemo(
    () => ({ user, error, ...actions }),
    [user, error, actions]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
